import uiI18n from "../i18n";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import MobileFilterSheet from "../components/MobileFilterSheet";
import {
  LuChevronDown,
  LuMapPin,
  LuRotateCcw,
  LuSearch,
  LuSlidersHorizontal,
} from "react-icons/lu";

const SearchForm = ({
  citiesAndDelegations,
  handleSearch,
  selectedCity,
  setSelectedCity,
  selectedDelegation,
  setSelectedDelegation,
  minPrice,
  setMinPrice,
  maxPrice,
  setMaxPrice,
  category,
  setCategory,
  libelle,
  setLibelle,
  rooms,
  setRooms,
}) => {
  const [isAdvancedSearch, setIsAdvancedSearch] =
    useState(false);

  const { t } = useTranslation();

  const roomOptions = [
    {
      value: "1",
      label: t("rooms.studio", "Studio"),
    },
    {
      value: "2",
      label: t("rooms.two", "2 pièces"),
    },
    {
      value: "3",
      label: t("rooms.three", "3 pièces"),
    },
    {
      value: "4",
      label: t("rooms.four", "4 pièces"),
    },
    {
      value: "5",
      label: t("rooms.five", "5 pièces"),
    },
    {
      value: "6",
      label: t(
        "rooms.six_or_more",
        "6 pièces et plus"
      ),
    },
  ];

  const handleCityChange = (event) => {
    setSelectedCity(event.target.value);
    setSelectedDelegation("");
  };

  const toggleAdvancedSearch = () => {
    setIsAdvancedSearch(
      (previousValue) => !previousValue
    );
  };

  const toggleRoom = (roomValue) => {
    setRooms((previousRooms) => {
      const currentRooms = Array.isArray(
        previousRooms
      )
        ? previousRooms
        : [];

      return currentRooms.includes(roomValue)
        ? currentRooms.filter(
            (room) => room !== roomValue
          )
        : [...currentRooms, roomValue];
    });
  };

  const submitSearch = () => {
    handleSearch({
      selectedCity,
      selectedDelegation,
      minPrice,
      maxPrice,
      category,
      libelle,
      rooms: Array.isArray(rooms) ? rooms : [],
    });
  };

  const resetSearch = () => {
    setSelectedCity("");
    setSelectedDelegation("");
    setMinPrice("");
    setMaxPrice("");
    setCategory("");
    setLibelle("");
    setRooms([]);

    handleSearch({
      selectedCity: "",
      selectedDelegation: "",
      minPrice: "",
      maxPrice: "",
      category: "",
      libelle: "",
      rooms: [],
    });
  };

  return (
    <MobileFilterSheet summary={<>
      <div className="phone-keyword"><button type="button" onClick={submitSearch} aria-label={t("search_form.search")}><LuSearch aria-hidden="true" /></button><input aria-label={t("search_form.label")} placeholder={t("search_form.search_placeholder")} value={libelle} onChange={e => setLibelle(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); submitSearch(); } }} /></div>
      <select className="phone-summary-city" aria-label={t("city")} value={selectedCity} onChange={handleCityChange}><option value="">{t("ui.all_governorates")}</option>{Object.keys(citiesAndDelegations || {}).map(city => <option key={city} value={city}>{t("cities." + city, city)}</option>)}</select>
      <select className="phone-summary-category" aria-label={t("search_form.category")} value={category} onChange={e => setCategory(e.target.value)}><option value="">{t("search_form.all_categories")}</option>{[["Appartement","apartment"],["Maison","house"],["Commercial","commercial"],["Terrain","land"],["Autres","others"]].map(([value,key]) => <option key={value} value={value}>{t(key)}</option>)}</select>

    </>}>
    <section
      className="lina-location-fields
        mb-8
        rounded-3xl
        border
        border-slate-200
        bg-white
        p-4
        shadow-sm
        sm:p-6
      "
    >
      {/* Titre */}
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-green-600"> {uiI18n.t("ui.property_search")} </p>

        <h2 className="mt-2 text-2xl font-black text-slate-900">
          {t(
            "search_form.search_title",
            "Trouvez votre futur logement"
          )}
        </h2>

        <p className="mt-2 text-sm leading-6 text-slate-500">
          {t(
            "search_form.search_description",
            "Sélectionnez une ville puis affinez votre recherche selon vos critères."
          )}
        </p>
      </div>

      {/* Recherche principale */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Ville */}
        <div>
          <label htmlFor="search-governorate" className="mb-2 block text-sm font-bold text-slate-700">
            {t("city", "Ville")}
          </label>

          <div className="relative">
            <LuMapPin
              size={18}
              className="
                pointer-events-none
                absolute
                start-4
                top-1/2
                -translate-y-1/2
                text-slate-400
              "
            />

            <select id="search-governorate"
              value={selectedCity}
              onChange={handleCityChange}
              className="
                h-12
                w-full
                appearance-none
                rounded-2xl
                border
                border-slate-200
                bg-slate-50
                ps-11
                pe-10
                text-sm
                font-medium
                text-slate-900
                outline-none
                transition
                hover:border-slate-300
                focus:border-green-500
                focus:bg-white
                focus:ring-4
                focus:ring-green-500/10
              "
            >
              <option value="">
                {t("city", "Toutes les villes")}
              </option>

              {Object.keys(
                citiesAndDelegations || {}
              ).map((city) => (
                <option
                  key={city}
                  value={city}
                >
                  {t(`cities.${city}`, city)}
                </option>
              ))}
            </select>

            <LuChevronDown
              size={16}
              className="
                pointer-events-none
                absolute
                end-4
                top-1/2
                -translate-y-1/2
                text-slate-400
              "
            />
          </div>
        </div>

        {/* Délégation */}
        <div>
          <label htmlFor="search-delegation" className="mb-2 block text-sm font-bold text-slate-700">
            {t("delegation", "Délégation")}
          </label>

          <div className="relative">
            <LuMapPin
              size={18}
              className="
                pointer-events-none
                absolute
                start-4
                top-1/2
                -translate-y-1/2
                text-slate-400
              "
            />

            <select id="search-delegation"
              value={selectedDelegation}
              onChange={(event) =>
                setSelectedDelegation(
                  event.target.value
                )
              }
              disabled={!selectedCity}
              className="
                h-12
                w-full
                appearance-none
                rounded-2xl
                border
                border-slate-200
                bg-slate-50
                ps-11
                pe-10
                text-sm
                font-medium
                text-slate-900
                outline-none
                transition
                hover:border-slate-300
                focus:border-green-500
                focus:bg-white
                focus:ring-4
                focus:ring-green-500/10
                disabled:cursor-not-allowed
                disabled:bg-slate-100
                disabled:text-slate-400
              "
            >
              <option value="">
                {t(
                  "delegation",
                  "Toutes les délégations"
                )}
              </option>

              {selectedCity &&
                citiesAndDelegations?.[
                  selectedCity
                ]?.map((delegation) => (
                  <option
                    key={delegation}
                    value={delegation}
                  >
                    {t(
                      `delegations.${selectedCity}.${delegation}`,
                      delegation
                    )}
                  </option>
                ))}
            </select>

            <LuChevronDown
              size={16}
              className="
                pointer-events-none
                absolute
                end-4
                top-1/2
                -translate-y-1/2
                text-slate-400
              "
            />
          </div>
        </div>

        {/* Catégorie */}
        <div>
          <label className="mb-2 block text-sm font-bold text-slate-700">
            {t(
              "search_form.category",
              "Catégorie"
            )}
          </label>

          <div className="relative">
            <select
              value={category}
              onChange={(event) =>
                setCategory(event.target.value)
              }
              className="
                h-12
                w-full
                appearance-none
                rounded-2xl
                border
                border-slate-200
                bg-slate-50
                px-4
                pe-10
                text-sm
                font-medium
                text-slate-900
                outline-none
                transition
                hover:border-slate-300
                focus:border-green-500
                focus:bg-white
                focus:ring-4
                focus:ring-green-500/10
              "
            >
              <option value="">
                {t(
                  "search_form.all_categories",
                  "Toutes les catégories"
                )}
              </option>

              <option value="Appartement">
                {t("apartment", "Appartement")}
              </option>

              <option value="Maison">
                {t("house", "Maison")}
              </option>

              <option value="Commercial">
                {t("commercial", "Commercial")}
              </option>

              <option value="Terrain">
                {t("land", "Terrain")}
              </option>

              <option value="Autres">
                {t("others", "Autres")}
              </option>
            </select>

            <LuChevronDown
              size={16}
              className="
                pointer-events-none
                absolute
                end-4
                top-1/2
                -translate-y-1/2
                text-slate-400
              "
            />
          </div>
        </div>

        {/* Recherche avancée */}
        <div className="flex items-end">
          <button
            type="button"
            onClick={toggleAdvancedSearch}
            aria-expanded={isAdvancedSearch}
            className="
              inline-flex
              h-12
              w-full
              items-center
              justify-center
              gap-2
              rounded-2xl
              border
              border-slate-200
              bg-white
              px-5
              text-sm
              font-bold
              text-slate-700
              transition
              hover:border-green-300
              hover:bg-green-50
              hover:text-green-700
              focus:outline-none
              focus:ring-4
              focus:ring-green-500/15
            "
          >
            <LuSlidersHorizontal size={18} />

            <span>
              {isAdvancedSearch
                ? t(
                    "search_form.hide_advanced_search",
                    "Masquer les filtres"
                  )
                : t(
                    "search_form.advanced_search",
                    "Recherche avancée"
                  )}
            </span>

            <LuChevronDown
              size={16}
              className={`transition-transform duration-200 ${
                isAdvancedSearch
                  ? "rotate-180"
                  : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* Recherche avancée */}
      {isAdvancedSearch && (
        <div
          className="
            mt-6
            rounded-2xl
            border
            border-slate-200
            bg-slate-50/70
            p-4
            sm:p-5
          "
        >
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {/* Libellé */}
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-bold text-slate-700">
                {t(
                  "search_form.label",
                  "Mot-clé"
                )}
              </label>

              <div className="relative">
                <LuSearch
                  size={18}
                  className="
                    pointer-events-none
                    absolute
                    start-4
                    top-1/2
                    -translate-y-1/2
                    text-slate-400
                  "
                />

                <input
                  type="text"
                  placeholder={t(
                    "search_form.search_placeholder",
                    "Appartement, villa, terrain..."
                  )}
                  value={libelle}
                  onChange={(event) =>
                    setLibelle(event.target.value)
                  }
                  className="
                    h-12
                    w-full
                    rounded-2xl
                    border
                    border-slate-200
                    bg-white
                    ps-11
                    pe-4
                    text-sm
                    text-slate-900
                    outline-none
                    transition
                    placeholder:text-slate-400
                    hover:border-slate-300
                    focus:border-green-500
                    focus:ring-4
                    focus:ring-green-500/10
                  "
                />
              </div>
            </div>

            {/* Pièces */}
            <div className="md:col-span-2">
              <label className="mb-3 block text-sm font-bold text-slate-700">
                {t(
                  "search_form.rooms",
                  "Nombre de pièces"
                )}
              </label>

              <div className="flex flex-wrap gap-2">
                {roomOptions.map((room) => {
                  const active =
                    Array.isArray(rooms) &&
                    rooms.includes(room.value);

                  return (
                    <button
                      key={room.value}
                      type="button"
                      onClick={() =>
                        toggleRoom(room.value)
                      }
                      className={`
                        rounded-xl
                        border
                        px-4
                        py-2
                        text-sm
                        font-semibold
                        transition-all
                        duration-200
                        ${
                          active
                            ? "border-green-600 bg-green-600 text-white shadow-md shadow-green-600/15"
                            : "border-slate-200 bg-white text-slate-600 hover:border-green-300 hover:bg-green-50 hover:text-green-700"
                        }
                      `}
                    >
                      {room.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Prix minimum */}
            <div>
              <label className="mb-2 block text-sm font-bold text-slate-700">
                {t(
                  "search_form.min_price",
                  "Prix minimum"
                )}
              </label>

              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="999999999"
                  value={minPrice}
                  onChange={(event) =>
                    setMinPrice(
                      event.target.value.slice(0, 9)
                    )
                  }
                  placeholder="0"
                  className="
                    h-12
                    w-full
                    rounded-2xl
                    border
                    border-slate-200
                    bg-white
                    px-4
                    pe-16
                    text-sm
                    text-slate-900
                    outline-none
                    transition
                    hover:border-slate-300
                    focus:border-green-500
                    focus:ring-4
                    focus:ring-green-500/10
                  "
                />

                <span className="pointer-events-none absolute end-4 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                  TND
                </span>
              </div>
            </div>

            {/* Prix maximum */}
            <div>
              <label className="mb-2 block text-sm font-bold text-slate-700">
                {t(
                  "search_form.max_price",
                  "Prix maximum"
                )}
              </label>

              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="999999999"
                  value={maxPrice}
                  onChange={(event) =>
                    setMaxPrice(
                      event.target.value.slice(0, 9)
                    )
                  }
                  placeholder="0"
                  className="
                    h-12
                    w-full
                    rounded-2xl
                    border
                    border-slate-200
                    bg-white
                    px-4
                    pe-16
                    text-sm
                    text-slate-900
                    outline-none
                    transition
                    hover:border-slate-300
                    focus:border-green-500
                    focus:ring-4
                    focus:ring-green-500/10
                  "
                />

                <span className="pointer-events-none absolute end-4 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                  TND
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="lina-m-filters-actions mt-6 flex flex-col-reverse gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={resetSearch}
          className="
            inline-flex
            h-12
            items-center
            justify-center
            gap-2
            rounded-2xl
            border
            border-slate-200
            bg-white
            px-6
            text-sm
            font-bold
            text-slate-600
            transition
            hover:border-slate-300
            hover:bg-slate-100
            hover:text-slate-900
            focus:outline-none
            focus:ring-4
            focus:ring-slate-500/10
          "
        >
          <LuRotateCcw size={17} />

          {t(
            "search_form.reset",
            "Réinitialiser"
          )}
        </button>

        <button
          type="button"
          data-filter-apply
          onClick={submitSearch}
          className="
            inline-flex
            h-12
            items-center
            justify-center
            gap-2
            rounded-2xl
            bg-green-600
            px-8
            text-sm
            font-extrabold
            text-white
            shadow-lg
            shadow-green-600/20
            transition-all
            hover:-translate-y-0.5
            hover:bg-green-700
            hover:shadow-xl
            focus:outline-none
            focus:ring-4
            focus:ring-green-500/20
          "
        >
          <LuSearch size={18} />

          {t(
            "search_form.search",
            "Rechercher"
          )}
        </button>
      </div>
    </section>
    </MobileFilterSheet>
  );
};

export default SearchForm;